/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/JobQaAreaDetail.tsx
 * FILE_ID: JOBHUB-QA-AREA-DETAIL-001
 * PURPOSE: One Areas-adjacent frame for Build, Run and ITP.
 * LANDMINE: Run does not mount the rail. Build and ITP still mount the same
 *      rail, including Add checklist, rename, the row menu, numbers, and
 *      checkboxes. isSelected takes (scope, id).
 */
import { useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import type { QaAreaExecution, QaFloor } from '../../qa/qaApi'
import { checklistGroups, normalizeList } from '../../qa/qaApiHelpers'
import { groupNameOf } from './qaTabSelectors'
import { QaChecklistTable } from '../../qa/QaChecklistTable'
import { AreaExecutionPanel } from '../JobQaExecution'
import { QaItpView } from './QaItpView'
import { QaLockedAreaBanner } from './QaLockedAreaBanner'
import { QaUndoBar } from './QaUndoBar'
import { type QaUndoableMove } from './useQaTreeMoves'
import { QaModePanes } from './build/QaModePanes'
import { useQaSiblingReorder } from './useQaSiblingReorder'
import { QaChecklistJumpList } from './QaChecklistJumpList'
import { useQaItpExecutions } from './useQaItpExecutions'
import { addBlankChecklist } from './addBlankChecklist'
import { QaNewChecklistDialog } from './build/QaNewChecklistDialog'
import type { JobQaTabController } from './useJobQaTabController'
import { type QaItpRequest, type QaItpUi } from './qaItpScope'
import { QA_BUILD_PANE_CLASS, QA_TABLE_SCROLL_CLASS } from './qaWorkspaceLayout'

export type { QaItpUi }

export function JobQaAreaDetail({
  controller,
  itpRequest,
  itpPending,
  onItpUi,
  showAll = false,
  onShowAll,
  startRenameChecklistId,
  onRenameStarted,
  onOpenChecklistMenu,
  checklistsCollapsed,
  onToggleChecklists,
}: {
  controller: JobQaTabController
  itpRequest: QaItpRequest
  itpAreas: QaAreaExecution[]
  itpPending: boolean
  onItpUi: (next: QaItpUi | null) => void
  showAll?: boolean
  onShowAll?: () => void
  startRenameChecklistId?: string | null
  onRenameStarted?: () => void
  onOpenChecklistMenu?: (
    checklistId: string,
    name: string,
    areaId: string,
    event: { clientX: number; clientY: number },
  ) => void
  checklistsCollapsed?: boolean
  onToggleChecklists?: () => void
}) {
  const { api, props, selection, tree, workspace } = controller
  const moves = useQaSiblingReorder(controller)
  const [draftOpen, setDraftOpen] = useState(false)
  const execution = workspace.areaExecQuery.data
  const allAreas = normalizeList<QaAreaExecution>(workspace.areasQuery.data)
  const { executions: jumpExecutions } = useQaItpExecutions(
    allAreas.map((area) => area.id),
    api,
    workspace.areaExecutionsReady,
  )
  const jumpRows = jumpExecutions.flatMap((area) => checklistGroups(area).map((group) => ({
    id: group.checklist.id,
    name: group.name,
    areaId: area.id,
    areaName: area.area_name,
  })))
  const locked = execution?.status === 'locked'
  const groups = execution
    ? checklistGroups(execution, undefined)
    : []
  const pendingScrollId = useRef<string | null>(null)
  const checklistIds = groups.map((group) => group.checklist.id)
  const checklistKey = checklistIds.join(',')
  useEffect(() => {
    const id = pendingScrollId.current
    if (!id) return
    const node = document.querySelector(`[data-qa-rail-checklist="${id}"]`)
    if (!node) return
    node.scrollIntoView({ block: 'nearest' })
    pendingScrollId.current = null
  }, [checklistKey])
  const selectedChecklistIds = new Set(selection.visibleOf('checklists', checklistIds))
  const activeChecklistId = workspace.allChecklists
    ? null
    : workspace.activeChecklistId && checklistIds.includes(workspace.activeChecklistId)
      ? workspace.activeChecklistId
      : null

  const unlock = useMutation({
    mutationFn: () => api.unlockArea(execution!.id, 'Unlock to edit'),
    onSuccess: workspace.invalidateExecution,
  })
  const addChecklist = useMutation({
    mutationFn: () => {
      if (!execution?.id) return Promise.resolve('')
      // Reused from: packages/job-hub/src/jobs/tabs/job-qa/addBlankChecklist.ts (addBlankChecklist)
      return addBlankChecklist(api, execution.id)
    },
    onSuccess: (id) => {
      if (!id) return
      workspace.setAllChecklists(false)
      workspace.setActiveChecklistId(id)
      pendingScrollId.current = id
      workspace.invalidateExecution()
    },
    onError: (err) => {
      controller.setError(err instanceof Error ? err.message : 'Could not add the checklist.')
    },
  })

  const openModal = (ids: string[], itemIds?: string[]) => {
    onItpUi({
      mode: 'modal',
      areaIds: execution ? [execution.id] : itpRequest.areaIds,
      checklistIds: ids,
      itemIds: itemIds ?? null,
    })
  }

  return (
    <div className="min-w-0 space-y-2">
      {locked && execution && (
        <QaLockedAreaBanner
          area={execution}
          unlocking={unlock.isPending}
          onUnlock={execution.can_sign_off ? () => unlock.mutate() : undefined}
        />
      )}
      {tree.undoable?.nodeType === 'checklist_instance' && (
        <QaUndoBar
          message={`Moved to ${tree.undoable.parentName}.`}
          busy={tree.undo.isPending}
          onUndo={() => tree.undo.mutate(tree.undoable as QaUndoableMove)}
          onDismiss={tree.dismissUndo}
        />
      )}
      {workspace.areaExecQuery.isLoading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden /> Loading checklist…
        </div>
      ) : execution ? (
        <>
          {workspace.areaMode !== 'build' ? null : (
          <details className="rounded-md border border-border">
            <summary className="cursor-pointer px-2 py-1 text-sm">
              {execution.group_name ? `${execution.group_name} / ` : ''}{execution.area_name}
              {activeChecklistId ? ` / ${groups.find((group) => group.checklist.id === activeChecklistId)?.name ?? ''}` : ''}
            </summary>
            <QaChecklistJumpList
              rows={jumpRows}
              currentAreaId={execution.id}
              currentChecklistId={activeChecklistId}
              onPick={(areaId, checklistId) => {
                if (areaId !== execution.id) workspace.selectArea(areaId)
                workspace.setAllChecklists(false)
                workspace.setActiveChecklistId(checklistId)
              }}
            />
          </details>
          )}
        <QaModePanes
          hideRail={workspace.areaMode !== 'build'}
          groups={groups}
          selectedId={activeChecklistId}
          onSelect={(id) => {
            if (!id) return
            workspace.setAllChecklists(false)
            workspace.setActiveChecklistId(id)
          }}
          onRename={locked ? undefined : (checklistId, title) => {
            void api.renameChecklist(checklistId, title).then(workspace.invalidateExecution)
          }}
          // Reused from: packages/job-hub/src/jobs/tabs/job-qa/addBlankChecklist.ts (addBlankChecklist)
          onAdd={controller.canBuild !== false && workspace.areaMode === 'build' && !locked
            ? () => { addChecklist.mutate() }
            : undefined}
          adding={addChecklist.isPending}
          selectedIds={selectedChecklistIds}
          onToggleSelected={(id) => selection.toggle('checklists', id)}
          startRenameId={startRenameChecklistId}
          onRenameStarted={onRenameStarted}
          areaId={execution.id}
          reorderFor={workspace.areaMode === 'build' && controller.canBuild !== false
            ? (checklistId) => moves.checklistMove(execution.id, checklistId, groups.map((group) => group.checklist.id))
            : undefined}
          onOpenMenu={onOpenChecklistMenu ? (checklistId, event) => {
            const name = groups.find((group) => group.checklist.id === checklistId)?.name ?? ''
            onOpenChecklistMenu(checklistId, name, execution.id, event)
          } : undefined}
          bodyClassName={workspace.areaMode === 'build' ? QA_BUILD_PANE_CLASS : undefined}
          checklistsCollapsed={checklistsCollapsed}
          onToggleChecklists={onToggleChecklists}
        >
          {draftOpen && workspace.areaMode === 'build' && (
            <QaNewChecklistDialog
              areaId={execution.id}
              api={api}
              onCreated={(id) => {
                workspace.setAllChecklists(false)
                workspace.setActiveChecklistId(id)
                setDraftOpen(false)
                requestAnimationFrame(() => {
                  document
                    .querySelector(`[data-qa-rail-checklist="${id}"]`)
                    ?.scrollIntoView({ block: 'nearest' })
                })
              }}
              onMutated={workspace.invalidateExecution}
              onCancel={() => setDraftOpen(false)}
            />
          )}
          {workspace.areaMode === 'build' ? (
            <QaChecklistTable
              area={execution}
              api={api}
              disabled={locked}
              checklistId={activeChecklistId}
              onInsertFromLibrary={activeChecklistId ? () => controller.requestOpenTemplates({
                checklistId: activeChecklistId,
                areaId: execution.id,
                name: groups.find((group) => group.checklist.id === activeChecklistId)?.name ?? 'checklist',
              }) : undefined}
              onMutated={workspace.invalidateExecution}
              onRenameChecklist={locked ? undefined : (checklistId, title) => {
                void api.renameChecklist(checklistId, title).then(workspace.invalidateExecution)
              }}
              itemSelection={{
                isSelected: (id, checklistId) => selection.isCheckIncluded(id, checklistId, execution.id),
                onToggle: (id, checklistId) => selection.toggleCheck(id, checklistId, execution.id),
              }}
              checklistSelection={{
                isSelected: (id) => selection.isSelected('checklists', id),
                onToggle: (id) => selection.toggle('checklists', id),
              }}
              startRenameId={startRenameChecklistId}
              onRenameStarted={onRenameStarted}
              scrollClassName={QA_TABLE_SCROLL_CLASS}
              onUndo={controller.history.canUndo ? () => { void controller.history.undo() } : undefined}
              onRedo={controller.history.canRedo ? () => { void controller.history.redo() } : undefined}
            />
          ) : workspace.areaMode === 'itp' ? (
            itpPending ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden /> Loading ITP…
              </div>
            ) : (
              <QaItpView
                areas={[execution]}
                pickerAreas={allAreas.map((row) => ({
                  id: row.id,
                  name: row.area_name,
                  group: groupNameOf(row, normalizeList<QaFloor>(workspace.floorsQuery.data)),
                }))}
                onSelectArea={(areaId) => workspace.selectArea(areaId)}
                checklistIds={activeChecklistId ? [activeChecklistId] : null}
                itemIds={itpRequest.itemIds ? new Set(itpRequest.itemIds) : undefined}
                showAll={showAll}
                onShowAll={onShowAll}
                jobId={props.jobId}
                inspectionId={workspace.activeInspectionId as string}
                api={api}
                onMutated={workspace.invalidateExecution}
                onSetStatus={workspace.setItemStatus}
                statusPending={workspace.setStatus.isPending}
                itemSelection={{
                  isSelected: (id, checklistId, areaId) => selection.isCheckIncluded(id, checklistId, areaId ?? execution.id),
                  onToggle: (id, checklistId, areaId) => selection.toggleCheck(id, checklistId, areaId ?? execution.id),
                }}
                checklistSelection={{
                  isSelected: (id) => selection.isSelected('checklists', id),
                  onToggle: (id) => selection.toggle('checklists', id),
                }}
                onRenameChecklist={locked ? undefined : (checklistId, title) => {
                  void api.renameChecklist(checklistId, title).then(workspace.invalidateExecution)
                }}
                startRenameId={startRenameChecklistId}
                onRenameStarted={onRenameStarted}
                onWriteError={controller.setError}
              />
            )
          ) : (
            <AreaExecutionPanel
              area={execution}
              checklistId={activeChecklistId}
              areas={allAreas.map((row) => ({
                id: row.id,
                name: row.area_name,
                group: groupNameOf(row, normalizeList<QaFloor>(workspace.floorsQuery.data)),
              }))}
              checklists={groups.map((group) => ({ id: group.checklist.id, name: group.name }))}
              onSelectArea={(areaId) => workspace.selectArea(areaId)}
              onSelectChecklist={(checklistId) => {
                workspace.setAllChecklists(false)
                workspace.setActiveChecklistId(checklistId)
              }}
              jobId={props.jobId}
              api={api}
              onMutated={workspace.invalidateExecution}
              onSetStatus={workspace.setItemStatus}
              onSetAreaStatus={(areaId, status) => workspace.setAreaWorkStatus.mutate({ areaId, status })}
              statusPending={workspace.setStatus.isPending || workspace.setAreaWorkStatus.isPending}
              onOpenItp={openModal}
              onWriteError={controller.setError}
              itemSelection={{
                isSelected: (id, checklistId) => selection.isCheckIncluded(id, checklistId, execution.id),
                onToggle: (id, checklistId) => selection.toggleCheck(id, checklistId, execution.id),
              }}
              checklistSelection={{
                isSelected: (id) => selection.isSelected('checklists', id),
                onToggle: (id) => selection.toggle('checklists', id),
              }}
              onRenameChecklist={locked ? undefined : (checklistId, title) => {
                void api.renameChecklist(checklistId, title).then(workspace.invalidateExecution)
              }}
              startRenameId={startRenameChecklistId}
              onRenameStarted={onRenameStarted}
            />
          )}
        </QaModePanes>
        </>
      ) : (
        <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Select an area to execute its checklist.
        </div>
      )}
    </div>
  )
}
